import { useState } from "react";

import moment from "moment-timezone";
import { FlagIcon } from "react-flag-kit";
import Dialog from "@material-ui/core/Dialog";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";

import { timeDisplayFormat } from "../../utils/utils";
import scheduleSequential from "../../assets/images/schedule/img_class-multiple-classes.png";
import scheduleAllocation from "../../assets/images/img_class-multiple-classes-allocation.png";
import { getConfirmClass, getNextClass, postConfirmClassFixed, postConfirmClassRandom } from "../../store/middleware";

function DayCalendarSchedules({ timeSchedules }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useSelector((state) => state.userReducer);
  const { classesRequest } = useSelector((state) => state.teacherReducer);

  const [alertMsg, setAlertMsg] = useState("");
  const [alertImg, setAlertImg] = useState("");
  const [alertTitle, setAlertTitle] = useState("");
  const [alertItem, setAlertItem] = useState(null);
  const [openAlert, setOpenAlert] = useState(false);
  const [alertClasses, setAlertClasses] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [itemSelected, setItemSelected] = useState(null);
  const [modalConfirm, setModalConfirm] = useState(false);
  const [alertRecurrencyMsg, setAlertRecurrencyMsg] = useState("");

  const userTimezoneConvert = (time) => {
    return moment.tz(time, user.timezone);
  };

  const acceptHandle = async (item) => {
    const {
      allocationSchedule,
      allocationScheduleId,
      sequentialSchedule,
      sequentialScheduleClassesCount,
      lingoLanguage: {
        flag,
        language: { name },
      },
    } = item;
    if (allocationSchedule === "fixed") {
      const classes = classesRequest
        .filter((item) => item.allocationScheduleId === allocationScheduleId)
        .map((item) => {
          const time = userTimezoneConvert(item.originalScheduledDateTimeUTC, "UTC", user);
          const initialDate = time.format("DD/MM/YYYY");
          const dayWeek = t(time.format("dddd").toUpperCase());
          const initialTime = time.format("h:mm a");
          const finalTime = time.clone().add(30, "minutes").format("h:mm a");
          const schedule = `${initialDate} • ${dayWeek} • ${initialTime} - ${finalTime}`;
          return { flag, language: name, schedule, dayWeek, time };
        });
      const dayWeeks = classes.reduce((days, item) => {
        if (days && days.indexOf(item.dayWeek) === -1) {
          return [...days, item.dayWeek];
        } else {
          return [...days];
        }
      }, []);
      let recurrencyMsg = `
          ${t("REPEAT")}: ${dayWeeks.map((item) => t(item.toUpperCase())).join(", ")}.
          ${t("UNTIL")} ${classes[classes.length - 1].time.format("DD/MM/YYYY")}
        `;
      const firstWeekClasses = classes.filter((item) => item.time.isSame(classes[0].time, "week"));
      if (classes.length > 1) {
        setOpenAlert(true);
        setAlertTitle("MULTYPLE_CLASSES");
        setAlertImg(scheduleAllocation);
        setAlertMsg("MULTYPLE_CLASSES_MESSAGE");
        setAlertItem(item);
        setAlertClasses(firstWeekClasses);
        setAlertRecurrencyMsg(recurrencyMsg);
      } else {
        return acceptedHandle(item);
      }
    } else if (sequentialSchedule) {
      const alertClasses = () => {
        const classes = [];
        const time = userTimezoneConvert(item.originalScheduledDateTimeUTC, "UTC", user);
        for (let i = 0; i < sequentialScheduleClassesCount; i++) {
          const initialDate = time
            .clone()
            .add(30 * i, "minutes")
            .format("DD/MM/YYYY");
          const initialDay = t(
            time
              .clone()
              .add(30 * i, "minutes")
              .format("dddd"),
          );
          const initialTime = time
            .clone()
            .add(30 * i, "minutes")
            .format("h:mm a");
          const finalTime = time
            .clone()
            .add(30 * (i + 1), "minutes")
            .format("h:mm a");
          const schedule = `${initialDate} • ${initialDay} • ${initialTime} - ${finalTime}`;
          classes.push({ flag, language: name, schedule });
        }
        return classes;
      };

      setOpenAlert(true);
      setAlertTitle("MULTYPLE_CLASSES");
      setAlertImg(scheduleSequential);
      setAlertMsg("MULTYPLE_CLASSES_MESSAGE");
      setAlertItem(item);
      setAlertClasses(alertClasses());
    } else {
      return acceptedHandle(item);
    }
  };

  const closeAlert = () => {
    setOpenAlert(false);
  };

  const acceptedHandle = async (item) => {
    const { sequentialScheduleId, allocationScheduleId, allocationSchedule } = item;

    if (allocationSchedule === "random") {
      dispatch(postConfirmClassRandom(user.teacherId, sequentialScheduleId))
        .then(() => {
          dispatch(getConfirmClass(user.teacherId));
          dispatch(getNextClass());
          setOpenAlert(false);
        })
        .catch((err) => {
          console.log("ERROR ON ACCEPT CLASS", err);
        });
    }
    if (allocationSchedule === "fixed") {
      dispatch(postConfirmClassFixed(user.teacherId, allocationScheduleId))
        .then(() => {
          dispatch(getConfirmClass(user.teacherId));
          dispatch(getNextClass());
          setOpenAlert(false);
        })
        .catch((err) => {
          console.log("ERROR ON ACCEPT CLASS", err);
        });
    }
  };

  return (
    <>
      {timeSchedules.map((item, index) => {
        const timeStart = userTimezoneConvert(item.originalScheduledDateTimeUTC, "UTC", user).format(
          timeDisplayFormat(user?.timeDisplayFormat),
        );
        const timeEnd = moment(item.scheduledEndDateTime).format(timeDisplayFormat(user?.timeDisplayFormat));
        return (
          <div
            key={item.id}
            className={`calendarSchedule
              ${item.status === "done" ? "done" : ""}
              ${item.status === "noShow" ? "noShow" : ""}
              ${item.status === "pending" ? "pending" : ""}
              ${item.status === "canceled" ? "outLimit" : ""}
              ${item.status === "canceledFree" ? "canceled" : ""}`}
            onClick={() => {
              setModalConfirm(!modalConfirm);
              setItemSelected(item);
              setSelectedIndex(index);
            }}
          >
            <p className="calendarScheduleTime">
              {`${timeStart} - ${timeEnd}`} {t(item?.lingoLanguage?.language?.name.toUpperCase())}{" "}
              {item.target === "demo" && item.target.toUpperCase()}
            </p>
            {modalConfirm && selectedIndex === index && (
              <div className="toScheduleBox">
                {item.target !== "demo" && itemSelected.status === "pending" && (
                  <button onClick={() => acceptHandle(itemSelected)} className=" new-button confirm">
                    {t("BTN_CONFIRM")} <i className="fa fa-check-circle-o" aria-hidden="true" />
                  </button>
                )}
                <button onClick={() => navigate(`/class-details/${itemSelected?.id}`)} className="new-button confirm">
                  {t("VIEW")} <i className="fa fa-angle-right" aria-hidden="true" />
                </button>
                <button onClick={() => setModalConfirm(!modalConfirm)} className="new-button cancel">
                  <i className="fa fa-angle-left" aria-hidden="true"></i> {t("BTN_CLOSE")}
                </button>
              </div>
            )}
          </div>
        );
      })}
      <Dialog open={openAlert} onClose={closeAlert} className="alert-dialog-slide">
        <DialogTitle className="alert-dialog-slide-Msg">{t(alertTitle.toUpperCase())}</DialogTitle>
        <DialogContent className="alert-dialog-slide-content">
          <img src={alertImg} alt="schedule" />
          <p className="alert-dialog-text">{t(alertMsg.toUpperCase())}</p>
          <hr />
          <div>
            {alertClasses.map(({ flag, language, schedule }) => (
              <div key={JSON.stringify(schedule)}>
                <p>
                  <FlagIcon code={flag} /> {`${t(language.toUpperCase())} ${schedule}`}
                </p>
              </div>
            ))}
          </div>
          <hr />
          {alertRecurrencyMsg && (
            <>
              <p className="alert-dialog-text-recurrency-message">{alertRecurrencyMsg}</p>
              <hr />
            </>
          )}
        </DialogContent>
        <DialogActions className="alert-dialog-slide-actions">
          <button className="new-button close" onClick={() => closeAlert()}>
            {t("CANCEL")}
          </button>
          <button className="new-button" onClick={() => acceptedHandle(alertItem)} autoFocus>
            {t("ACCEPT_SEQUENCE")}
          </button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default DayCalendarSchedules;
